import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import CountCard from 'components/common/CountCard';
import styles from './MeetStatsChart.module.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const MeetStatsChart = ({
  statsData,
  loading = false,
  meetType = 'single-club',
  meetStatus = 'Upcoming',
  error = null,
}) => {
  const [chartData, setChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);



  // Transform data for Chart.js
  useEffect(() => {
    if (!statsData?.ageGroupStats) return;

    const ageGroups = statsData.ageGroupStats.map((item) => item.ageGroup);
    const maleData = statsData.ageGroupStats.map((item) => item.maleCount);
    const femaleData = statsData.ageGroupStats.map((item) => item.femaleCount);
    const totalData = statsData.ageGroupStats.map((item) => item.totalCount);

    const data = {
      labels: ageGroups,
      datasets: [
        {
          label: 'Male',
          data: maleData,
          backgroundColor: '#3b82f6',
          borderRadius: { topLeft: 0, topRight: 0 },
          barPercentage: 0.8,
          categoryPercentage: 0.9,
        },
        {
          label: 'Female',
          data: femaleData,
          backgroundColor: '#ec4899',
          borderRadius: { topLeft: 4, topRight: 4 },
          barPercentage: 0.8,
          categoryPercentage: 0.9,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 13,
              weight: 600,
            },
            padding: 5,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          max: Math.max(...totalData) + 5,
          grid: {
            color: '#f3f4f6',
            drawBorder: false,
          },
          ticks: {
            color: '#6b7280',
            font: {
              size: 11,
            },
            stepSize: 5,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            afterTitle: function (context) {
              const index = context[0].dataIndex;
              return (
                'Total: ' +
                totalData[index] +
                (totalData[index] === 1 ? ' athlete' : ' athletes')
              );
            },
          },
        },
        // Custom plugin to show values on bars
        afterDatasetsDraw: function (chart) {
          const ctx = chart.ctx;

          // Draw values on bars
          chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((bar, index) => {
              const data = dataset.data[index];
              if (data > 0) {
                ctx.save();
                ctx.fillStyle = 'white';
                ctx.font =
                  'bold 13px -apple-system, BlinkMacSystemFont, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                const x = bar.x;
                const y = bar.y + bar.height / 2;
                ctx.fillText(data, x, y);
                ctx.restore();
              }
            });
          });
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [statsData]);

  if (loading) {
    return (
      <div className={styles.statsPanel}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading meet statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.statsPanel}>
        <div className={styles.errorContainer}>
          <p>Failed to load meet statistics: {error}</p>
        </div>
      </div>
    );
  }

  if (!statsData) {
    return null;
  }

  return (
    <div className={styles.statsPanel}>
      {/* Left: Counts */}
      <div className={styles.countsSection}>
        {meetType === 'multi-club' && ( // if meet type is multi club
          <CountCard
            value={statsData.totalClubs}
            label="Club"
            animated={true}
          />
        )}
        {(() => {
          const athleteCount = meetType === 'multi-club' 
            ? statsData.totalAthletes
            : statsData.totalEligibleAthletes;
          
          // Determine the label and pluralization rules based on meet type and status
          let displayLabel, disablePluralization = false;
          
          if (meetType === 'multi-club') {
            displayLabel = 'Athlete';
          } else if (meetStatus === 'Upcoming' || meetStatus === 'Draft') {
            displayLabel = 'Eligible Athlete';
          } else if (meetStatus === 'Complete') {
            // For complete meets, show "Athletes Participated" with custom pluralization
            displayLabel = athleteCount === 1 ? 'Athlete Participated' : 'Athletes Participated';
            disablePluralization = true; // We handle pluralization manually
          } else {
            displayLabel = 'Athlete';
          }
          
          return (
            <CountCard
              value={athleteCount}
              label={displayLabel}
              animated={true}
              disablePluralization={disablePluralization}
            />
          );
        })()}
      </div>

      {/* Right: Chart */}
      <div className={styles.chartSection}>
        <div className={styles.chartContainer}>
          {chartData && chartOptions ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div className={styles.chartPlaceholder}>
              <p>Loading chart...</p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className={styles.chartLegend}>
          <div className={styles.legendItem}>
            <div className={`${styles.legendColor} ${styles.male}`}></div>
            <div className={styles.legendText}>
              Male{' '}
              <span className={styles.legendCount}>
                ({statsData.totalMaleAthletes} athlete
                {statsData.totalMaleAthletes === 1 ? '' : 's'})
              </span>
            </div>
          </div>
          <div className={styles.legendItem}>
            <div className={`${styles.legendColor} ${styles.female}`}></div>
            <div className={styles.legendText}>
              Female{' '}
              <span className={styles.legendCount}>
                ({statsData.totalFemaleAthletes} athlete
                {statsData.totalFemaleAthletes === 1 ? '' : 's'})
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

MeetStatsChart.propTypes = {
  statsData: PropTypes.shape({
    clubID: PropTypes.string,
    totalEligibleAthletes: PropTypes.number,
    totalMaleAthletes: PropTypes.number,
    totalFemaleAthletes: PropTypes.number,
    totalAgeGroups: PropTypes.number,
    ageGroupStats: PropTypes.arrayOf(
      PropTypes.shape({
        ageGroup: PropTypes.string,
        maleCount: PropTypes.number,
        femaleCount: PropTypes.number,
        totalCount: PropTypes.number,
      })
    ),
  }),
  loading: PropTypes.bool,
  meetType: PropTypes.string,
  meetStatus: PropTypes.string,
  error: PropTypes.string,
};

export default MeetStatsChart;
